import { Anchor, Content, Stack, Strong, Text } from '@kinesis/bungle';
import { Table, TableColumn } from '@kinesis/bungle/nursery';
import { DownloadButton } from '@/components/download-button';
import { DisclosureMethodologyPage, Unit } from '@/types';
import { renderMeasureClass } from '@/data/labels';
import { renderUnit } from '@/data/units';
import { formatDateOnlyString } from '@/date';

// Composes a coefficient as "0.79 kg CO₂-e/kWh" — kg CO₂-e per the measure's
// own unit (raw, deliberately NOT the report's kt scale). Significant-digit
// formatting so both ~0.79 and ~0.004 read sensibly.
const coefficient = (value: number, unit: Unit) =>
  `${value.toLocaleString(undefined, {
    maximumSignificantDigits: 4,
  })} kg CO₂-e/${renderUnit(unit)}`;

// One row of the emission-factor table: a single coefficient step of a single
// factor, flattened so the whole page is ONE aligned table (a measure with
// several factors, or a factor with several in-period steps, is just several
// rows). The measure carries its own unit because the coefficient denominator
// varies per measure (kWh, GJ, people, …).
type FactorRow = {
  key: string;
  measure: string;
  measureClass: DisclosureMethodologyPage['measures'][number]['measureClass'];
  unit: Unit;
  factor: string;
  scope: number;
  from: string;
  coefficient: number;
  sourceName?: string;
  sourceLink?: string;
};

// Flattens the grouped wire (measures → factors → steps) into one row per
// factor-step, in the wire's order (measure, then factor, then step). The
// table renders this order verbatim — a reference list, never ranked by value.
const flatten = (pageData: DisclosureMethodologyPage): FactorRow[] =>
  pageData.measures.flatMap((measure) =>
    measure.factors.flatMap((factor) =>
      factor.values.map((step) => ({
        key: `${measure.measure}|${factor.scope}|${factor.name}|${step.from}`,
        measure: measure.measure,
        measureClass: measure.measureClass,
        unit: measure.unit,
        factor: factor.name,
        scope: factor.scope,
        from: step.from,
        coefficient: step.coefficient,
        sourceName: step.sourceName,
        sourceLink: step.sourceLink,
      })),
    ),
  );

// The step's source: a link when `sourceLink` is present, plain text when only
// a name is given, "—" when absent (no fabricated citation).
const renderSource = (row: FactorRow) => {
  if (row.sourceName === undefined) return '—';
  if (row.sourceLink === undefined) return row.sourceName;
  return (
    <Anchor href={row.sourceLink} target='_blank'>
      {row.sourceName}
    </Anchor>
  );
};

// One row of the evidence table: a single uploaded document, flattened so the
// section is ONE aligned table (a source with several files is several rows,
// its label repeated).
type EvidenceRow = {
  key: string;
  source: string;
  id: number;
  filename: string;
  format: string;
  uploaded: string;
};

const flattenEvidence = (pageData: DisclosureMethodologyPage): EvidenceRow[] =>
  pageData.evidence.flatMap((source) =>
    source.files.map((file) => ({
      key: `${source.source}|${file.id}`,
      source: source.source,
      id: file.id,
      filename: file.filename,
      format: file.format,
      uploaded: file.uploaded,
    })),
  );

// The one-row shape of the "Methodology reference" section: the effective
// scenario's document, when it holds one. `scenario` is the effective
// scenario id the download route is keyed on.
type DocumentRow = {
  key: string;
  scenario: number;
  filename: string;
  format: string;
};

const documentRows = (pageData: DisclosureMethodologyPage): DocumentRow[] =>
  pageData.document === undefined
    ? []
    : [
        {
          key: pageData.document.filename,
          scenario: pageData.document.scenario,
          filename: pageData.document.filename,
          format: pageData.document.format,
        },
      ];

// Renders the "Methodology" page: the emission factors used in the (scoped)
// report, as a single table — one row per factor-step. Each row leads with the
// measure (its class, then the measure key as secondary text), then the
// factor, scope, the date the coefficient took effect, the coefficient (raw kg
// CO₂-e per the measure's unit), and the source as a link. A mid-period
// coefficient change reads as two rows with their own from-dates.
// Store-less per the component/page split; the page owns the RTK call (the
// page passes `onDownloadEvidence`, which fires the download mutation).
const DisclosureMethodology = (props: DisclosureMethodologyProps) => {
  const {
    pageData,
    onDownloadEvidence,
    onDownloadMethodology,
    downloadingEvidenceId,
    downloadingMethodology = false,
  } = props;
  const rows = flatten(pageData);
  const evidence = flattenEvidence(pageData);
  const documents = documentRows(pageData);
  return (
    <Stack space='large'>
      <Stack space='medium'>
        <Strong>Methodology reference</Strong>
        <Content
          background='white'
          padding='medium'
          border
          paddingMode='equal'
          borderRadiusSize='small'
        >
          {documents.length === 0 ? (
            <Text tone='secondary'>
              No methodology reference for this report.
            </Text>
          ) : (
            <Table data={documents} itemKey='key' header showGridLines>
              <TableColumn
                heading='File'
                renderItem={(row: DocumentRow) => row.filename}
              />
              <TableColumn
                heading='Format'
                renderItem={(row: DocumentRow) => row.format.toUpperCase()}
              />
              <TableColumn
                heading=''
                alignX='right'
                renderItem={(row: DocumentRow) => (
                  <DownloadButton
                    magnitude='small'
                    downloading={downloadingMethodology}
                    onClick={() => onDownloadMethodology(row.scenario)}
                  >
                    Download
                  </DownloadButton>
                )}
              />
            </Table>
          )}
        </Content>
      </Stack>
      <Stack space='medium'>
        <Strong>Emission factors</Strong>
        <Content
          background='white'
          padding='medium'
          border
          paddingMode='equal'
          borderRadiusSize='small'
        >
          <Table data={rows} itemKey='key' header showGridLines>
            <TableColumn
              heading='Measure'
              renderItem={(row: FactorRow) => (
                <Text>
                  {renderMeasureClass(row.measureClass)}{' '}
                  <Text tone='secondary'>({row.measure})</Text>
                </Text>
              )}
            />
            <TableColumn
              heading='Factor'
              renderItem={(row: FactorRow) => row.factor}
            />
            <TableColumn
              heading='Scope'
              renderItem={(row: FactorRow) => `Scope ${row.scope}`}
            />
            <TableColumn
              heading='In effect from'
              renderItem={(row: FactorRow) => formatDateOnlyString(row.from)}
            />
            <TableColumn
              heading='Coefficient'
              alignX='right'
              renderItem={(row: FactorRow) =>
                coefficient(row.coefficient, row.unit)
              }
            />
            <TableColumn
              heading='Source'
              renderItem={(row: FactorRow) => renderSource(row)}
            />
          </Table>
        </Content>
      </Stack>
      <Stack space='medium'>
        <Strong>Evidence</Strong>
        <Content
          background='white'
          padding='medium'
          border
          paddingMode='equal'
          borderRadiusSize='small'
        >
          {evidence.length === 0 ? (
            <Text tone='secondary'>No source documents for this report.</Text>
          ) : (
            <Table data={evidence} itemKey='key' header showGridLines>
              <TableColumn
                heading='Source'
                renderItem={(row: EvidenceRow) => row.source}
              />
              <TableColumn
                heading='File'
                renderItem={(row: EvidenceRow) => row.filename}
              />
              <TableColumn
                heading='Format'
                renderItem={(row: EvidenceRow) => row.format.toUpperCase()}
              />
              <TableColumn
                heading='Uploaded'
                renderItem={(row: EvidenceRow) =>
                  formatDateOnlyString(row.uploaded)
                }
              />
              <TableColumn
                heading=''
                alignX='right'
                renderItem={(row: EvidenceRow) => (
                  <DownloadButton
                    magnitude='small'
                    downloading={row.id === downloadingEvidenceId}
                    onClick={() => onDownloadEvidence(row.id)}
                  >
                    Download
                  </DownloadButton>
                )}
              />
            </Table>
          )}
        </Content>
      </Stack>
    </Stack>
  );
};

export type DisclosureMethodologyProps = {
  pageData: DisclosureMethodologyPage;
  onDownloadEvidence: (evidenceId: number) => void;
  // Fires the methodology-reference download for the page's document --
  // keyed on the EFFECTIVE scenario id the document carries (the one
  // scenario-keyed download route; no disclosure-scoped variant).
  onDownloadMethodology: (scenarioId: number) => void;
  // The evidence row whose download is in flight — that row's button spins;
  // undefined when idle. Per-row because the rows share one mutation.
  downloadingEvidenceId?: number;
  // The methodology-reference download is in flight (a single document row,
  // so a flag suffices).
  downloadingMethodology?: boolean;
};

export { DisclosureMethodology };
